/**
 * One generic "evidence peek" surface (§17). Any chip or lineage node calls
 * `openSource`/`openValue`; a single `EvidencePeekPanel` slides in and renders
 * per-kind styling. A small navigation stack lets a value → its source → back
 * without ever spawning per-source viewer pages.
 */

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

import type { CitedValue, SourceRef } from "../../types/citationArtifact";
import { EvidencePeekPanel } from "./EvidencePeekPanel";

export type PeekTarget =
	| { type: "source"; ref: SourceRef }
	| { type: "value"; value: CitedValue };

interface EvidencePeekContextValue {
	openSource: (ref: SourceRef) => void;
	openValue: (value: CitedValue) => void;
	close: () => void;
}

const EvidencePeekContext = createContext<EvidencePeekContextValue | null>(null);

interface EvidencePeekProviderProps {
	children: ReactNode;
	/** Chat id threaded to source deep-links (spreadsheet/pdf viewers). */
	chatId?: string;
}

export function EvidencePeekProvider({
	children,
	chatId,
}: EvidencePeekProviderProps) {
	const [stack, setStack] = useState<PeekTarget[]>([]);

	const openSource = useCallback((ref: SourceRef) => {
		setStack((prev) => [...prev, { type: "source", ref }]);
	}, []);
	const openValue = useCallback((value: CitedValue) => {
		setStack((prev) => [...prev, { type: "value", value }]);
	}, []);
	const close = useCallback(() => setStack([]), []);
	const back = useCallback(() => setStack((prev) => prev.slice(0, -1)), []);

	const api = useMemo<EvidencePeekContextValue>(
		() => ({ openSource, openValue, close }),
		[openSource, openValue, close],
	);

	const active = stack.length > 0 ? stack[stack.length - 1] : null;

	return (
		<EvidencePeekContext.Provider value={api}>
			{children}
			<EvidencePeekPanel
				target={active}
				canBack={stack.length > 1}
				chatId={chatId}
				onBack={back}
				onClose={close}
			/>
		</EvidencePeekContext.Provider>
	);
}

/**
 * Access the peek surface. Returns `null` when no provider is mounted, so chips
 * used outside a peek context render as plain (non-clickable) labels instead of
 * throwing.
 */
export function useEvidencePeek(): EvidencePeekContextValue | null {
	return useContext(EvidencePeekContext);
}
